import { StrictMode, useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import { HelmetProvider } from 'react-helmet-async'
import { BrowserRouter, useLocation } from 'react-router-dom'
import { App } from './App'
import { AuthProvider } from './lib/auth'
import './index.css'
import {
  checkForNewVersion,
  isChunkLoadError,
  isNewVersionAvailable,
  reloadForStaleChunk,
  startFreshBundleWatch,
} from './lib/stale-chunk'

// After a deploy, a browser still on the previous bundle requests a JS chunk whose
// hash no longer exists → recover by reloading once. Failures arrive through three
// channels, so listen on all (the ErrorBoundary handles the React.lazy render-throw).
// The reload is time-boxed in reloadForStaleChunk() so a later deploy still self-heals.
window.addEventListener('vite:preloadError', (e) => {
  e.preventDefault() // stop Vite re-throwing; we own the recovery
  reloadForStaleChunk()
})
window.addEventListener('error', (e) => {
  if (isChunkLoadError((e as ErrorEvent).error ?? (e as ErrorEvent).message)) reloadForStaleChunk()
})
window.addEventListener('unhandledrejection', (e) => {
  if (isChunkLoadError((e as PromiseRejectionEvent).reason)) reloadForStaleChunk()
})

// Stale-chunk PREVENTION: poll /version.json; when a newer deploy is live, reload at the
// NEXT route change — a graceful moment that pre-empts a chunk 404 instead of recovering
// after one. Poll on mount + every 5 min + when the tab regains focus.
function VersionGuard() {
  const { pathname } = useLocation()

  useEffect(() => {
    void checkForNewVersion()
    const id = window.setInterval(() => void checkForNewVersion(), 5 * 60_000)
    const onFocus = () => void checkForNewVersion()
    window.addEventListener('focus', onFocus)
    return () => {
      window.clearInterval(id)
      window.removeEventListener('focus', onFocus)
    }
  }, [])

  // biome-ignore lint/correctness/useExhaustiveDependencies: re-check for a stale bundle on every navigation, so pathname is the trigger on purpose
  useEffect(() => {
    if (isNewVersionAvailable()) reloadForStaleChunk()
  }, [pathname])

  return null
}

startFreshBundleWatch()

// Tells the index.html bootstrap watchdog the app actually mounted, so it never does a spurious
// reload. If this never fires (a stale-chunk boot failure), the watchdog recovers with one reload.
function BootSignal() {
  useEffect(() => {
    document.documentElement.dataset.appReady = '1'
    ;(window as unknown as { __navoBooted__?: () => void }).__navoBooted__?.()
  }, [])
  return null
}

const el = document.getElementById('root')
if (el)
  createRoot(el).render(
    <StrictMode>
      <HelmetProvider>
        <BrowserRouter>
          <AuthProvider>
            <BootSignal />
            <VersionGuard />
            <App />
          </AuthProvider>
        </BrowserRouter>
      </HelmetProvider>
    </StrictMode>,
  )
